import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { AlertConfig } from '../contacts/user.service';
import {
  DEFAULT_ALERT_CONFIG,
  DEFAULT_NOTIFY_WINDOW,
  DEFAULT_TARGET_TIME,
  PLATFORM_ALERT_LIMITS,
  resolveAlertConfig,
  SMS_BODY_MAX,
} from './configuration.defaults';
import {
  flagsToNotifyChannel,
  notifyChannelToFlags,
  NotifyChannel,
} from './notify-channel.util';
import {
  fromRetryIntervalMinutes,
  IntervalUnit,
  isRetryIntervalAtLeast,
  toRetryIntervalMinutes,
} from './interval-unit.util';
import { smsTokensValidator } from './sms-tokens.util';

export type AlertConfigFormControls = {
  retryAttempts: FormControl<number | null>;
  retryInterval: FormControl<number | null>;
  retryIntervalUnit: FormControl<IntervalUnit>;
  targetTime: FormControl<string>;
  notifyWindowStart: FormControl<string>;
  notifyWindowEnd: FormControl<string>;
  notifyWindowTimeZone: FormControl<string>;
  notifyChannel: FormControl<NotifyChannel>;
  smsBody: FormControl<string>;
  pressZeroToConnect: FormControl<boolean>;
  urlTitle: FormControl<string>;
  urlHyperlink: FormControl<string>;
};

export type AlertConfigFormGroup = FormGroup<AlertConfigFormControls>;

function retryIntervalAtLeastMinutes(minMinutes: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const raw = control.value;
    if (raw === null || raw === undefined || raw === '') {
      return null;
    }
    const value = Number(raw);
    if (!Number.isFinite(value)) {
      return null;
    }
    const unit = (control.parent?.get('retryIntervalUnit')?.value ?? 'minutes') as IntervalUnit;
    if (!isRetryIntervalAtLeast(value, unit, minMinutes)) {
      return { minInterval: { minMinutes } };
    }
    return null;
  };
}

const RETRY_ATTEMPTS_VALIDATORS = [
  Validators.required,
  Validators.min(1),
  Validators.max(PLATFORM_ALERT_LIMITS.maxRetryAttempts),
];

const RETRY_INTERVAL_VALIDATORS = [
  Validators.required,
  Validators.min(1),
  Validators.pattern(/^[1-9]\d*$/),
  retryIntervalAtLeastMinutes(PLATFORM_ALERT_LIMITS.minRetryIntervalMinutes),
];

export function createAlertConfigFormGroup(): AlertConfigFormGroup {
  return new FormGroup<AlertConfigFormControls>({
    retryAttempts: new FormControl<number | null>(null, {
      validators: RETRY_ATTEMPTS_VALIDATORS,
    }),
    retryInterval: new FormControl<number | null>(null, {
      validators: RETRY_INTERVAL_VALIDATORS,
    }),
    retryIntervalUnit: new FormControl<IntervalUnit>('minutes', { nonNullable: true }),
    targetTime: new FormControl(DEFAULT_TARGET_TIME, { nonNullable: true }),
    notifyWindowStart: new FormControl(DEFAULT_NOTIFY_WINDOW.start, {
      nonNullable: true,
      validators: [Validators.required],
    }),
    notifyWindowEnd: new FormControl(DEFAULT_NOTIFY_WINDOW.end, {
      nonNullable: true,
      validators: [Validators.required],
    }),
    notifyWindowTimeZone: new FormControl(DEFAULT_NOTIFY_WINDOW.timeZone, {
      nonNullable: true,
      validators: [Validators.required],
    }),
    notifyChannel: new FormControl<NotifyChannel>('both', { nonNullable: true }),
    smsBody: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(SMS_BODY_MAX), smsTokensValidator],
    }),
    pressZeroToConnect: new FormControl(DEFAULT_ALERT_CONFIG.pressZeroToConnect, {
      nonNullable: true,
    }),
    urlTitle: new FormControl('', { nonNullable: true }),
    urlHyperlink: new FormControl('', { nonNullable: true }),
  });
}

export function patchAlertConfigForm(form: AlertConfigFormGroup, config: AlertConfig): void {
  const interval = fromRetryIntervalMinutes(config.retryIntervalMinutes);
  form.reset({
    retryAttempts: config.retryAttempts,
    retryInterval: interval.value,
    retryIntervalUnit: interval.unit,
    smsBody: config.smsBody,
    pressZeroToConnect: config.pressZeroToConnect,
    urlTitle: config.urlTitle ?? '',
    urlHyperlink: config.urlHyperlink ?? '',
    targetTime: config.targetTime ?? DEFAULT_TARGET_TIME,
    notifyWindowStart: config.notifyWindow?.start ?? DEFAULT_NOTIFY_WINDOW.start,
    notifyWindowEnd: config.notifyWindow?.end ?? DEFAULT_NOTIFY_WINDOW.end,
    notifyWindowTimeZone: config.notifyWindow?.timeZone ?? DEFAULT_NOTIFY_WINDOW.timeZone,
    notifyChannel: flagsToNotifyChannel(config.noText, config.noCall),
  });
  updateTargetTimeValidators(form);
}

export function alertConfigFromForm(
  form: AlertConfigFormGroup,
  subjectLabels: { subjectLabel: string; subjectLabelPlural: string },
): AlertConfig {
  const value = form.getRawValue();
  const channelFlags = notifyChannelToFlags(value.notifyChannel);
  return resolveAlertConfig({
    subjectLabel: subjectLabels.subjectLabel,
    subjectLabelPlural: subjectLabels.subjectLabelPlural,
    retryAttempts: Number(value.retryAttempts),
    retryIntervalMinutes: toRetryIntervalMinutes(Number(value.retryInterval), value.retryIntervalUnit),
    smsBody: value.smsBody.trim(),
    pressZeroToConnect: value.pressZeroToConnect,
    urlTitle: value.urlTitle.trim(),
    urlHyperlink: value.urlHyperlink.trim(),
    notifyWindow: {
      start: value.notifyWindowStart.trim(),
      end: value.notifyWindowEnd.trim(),
      timeZone: value.notifyWindowTimeZone.trim(),
    },
    targetTime: value.retryIntervalUnit === 'day' ? value.targetTime.trim() : undefined,
    noText: channelFlags.noText,
    noCall: channelFlags.noCall,
  });
}

export function showDayTargetTime(form: AlertConfigFormGroup): boolean {
  return form.controls.retryIntervalUnit.value === 'day';
}

export function updateTargetTimeValidators(form: AlertConfigFormGroup): void {
  const control = form.controls.targetTime;
  if (showDayTargetTime(form)) {
    control.setValidators([
      Validators.required,
      Validators.pattern(/^([01]\d|2[0-3]):[0-5]\d$/),
    ]);
  } else {
    control.clearValidators();
  }
  control.updateValueAndValidity({ emitEvent: false });
}

export function intervalInputMin(form: AlertConfigFormGroup): number {
  return form.controls.retryIntervalUnit.value === 'minutes'
    ? PLATFORM_ALERT_LIMITS.minRetryIntervalMinutes
    : 1;
}

/** When custom campaign schedule is used, retry interval fields are hidden — keep values but skip validation. */
export function setAlertConfigRetryScheduleActive(form: AlertConfigFormGroup, active: boolean): void {
  const retryControls = [
    form.controls.retryAttempts,
    form.controls.retryInterval,
    form.controls.retryIntervalUnit,
    form.controls.targetTime,
  ];
  if (active) {
    form.controls.retryAttempts.setValidators(RETRY_ATTEMPTS_VALIDATORS);
    form.controls.retryInterval.setValidators(RETRY_INTERVAL_VALIDATORS);
    retryControls.forEach((control) => control.enable({ emitEvent: false }));
    updateTargetTimeValidators(form);
  } else {
    retryControls.forEach((control) => {
      control.clearValidators();
      control.disable({ emitEvent: false });
    });
  }
  form.updateValueAndValidity({ emitEvent: false });
}
